import clsx from 'clsx';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Surface } from './Surface';
import { IconChevronDown, IconLock } from './icons';

/**
 * Journey: a sequence of numbered phases, each a card of steps, that opens
 * one phase at a time. The phase being worked on is open; phases finished
 * earlier fold to a one-line summary that can be reopened; phases still
 * ahead sit closed with the reason they are closed. Used by Getting started.
 *
 * - `JourneyProgress` — the headline card: a bold "n of m done", the
 *   summary line, and a bar whose fill grows into place.
 * - `JourneyPhase`    — one phase card. `state` decides the marker (number,
 *   accent number, or check) and whether the steps show. Done phases start
 *   folded; `defaultOpen` overrides.
 * - `JourneyRow`      — one step: status glyph · title and detail · status ·
 *   action. The caller decides which action is the primary. With a `panel`
 *   the step opens in place, so the store does it without leaving the page.
 * - `StepCheck`       — the done glyph; `animate` draws it in for a step
 *   finished during this visit (see `useJustCompleted`).
 * - `Collapse`        — a block that opens and closes in place.
 *
 * Motion is CSS only (tokens.css: `v2-enter`, `v2-pop-in`, `v2-check-draw`,
 * `v2-grow`, `v2-collapse`, `v2-step*`) and switches off under
 * `prefers-reduced-motion`.
 */
export type JourneyPhaseState = 'done' | 'active' | 'upcoming';

/** A fill that starts at zero on first paint and grows to its value. */
function useGrownWidth(fraction: number) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setReady(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);
  return ready ? `${Math.min(Math.max(fraction, 0), 1) * 100}%` : '0%';
}

export const JourneyProgress: React.FC<{
  done: number;
  total: number;
  /** Uppercase micro-label above the figure. */
  label?: React.ReactNode;
  /** One line beside the figure: what is left. */
  summary?: React.ReactNode;
  /** Right-hand slot: the overall status. */
  status?: React.ReactNode;
  className?: string;
}> = ({
  done,
  total,
  label = 'Setup progress',
  summary,
  status,
  className,
}) => {
  const doneWidth = useGrownWidth(total ? done / total : 0);
  const complete = total > 0 && done >= total;
  return (
    <Surface padded className={className}>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <span className="block text-v2-10 font-semibold uppercase tracking-[0.1em] text-v2-fg-3">
            {label}
          </span>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <b className="v2-num text-v2-20 font-bold tracking-[-0.015em] text-v2-fg">
              {done} of {total} done
            </b>
            {summary && (
              <span className="text-v2-13 text-v2-fg-3">{summary}</span>
            )}
          </div>
        </div>
        {status && <div className="shrink-0 pt-0.5">{status}</div>}
      </div>
      <div
        role="progressbar"
        aria-label={typeof label === 'string' ? label : 'Progress'}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        className="mt-4 flex h-2 overflow-hidden rounded-full bg-v2-line/80"
      >
        <div
          className={clsx(
            'v2-grow h-full rounded-full',
            complete ? 'bg-v2-success' : 'bg-v2-accent',
          )}
          style={{ width: doneWidth }}
        />
      </div>
    </Surface>
  );
};

/**
 * The phase's marker: its number, filled with the accent while the phase is
 * open, hollow while it waits its turn, and a check once it is done.
 */
export const PhaseMarker: React.FC<{
  number: number;
  state: JourneyPhaseState;
  /** The marker just became a check: play the settle-in. */
  celebrate?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}> = ({ number, state, celebrate, size = 'md', className }) => (
  <span
    aria-hidden
    className={clsx(
      'v2-num inline-flex shrink-0 items-center justify-center rounded-full font-bold transition-colors duration-200',
      size === 'md' ? 'h-7 w-7 text-v2-13' : 'h-6 w-6 text-v2-12',
      state === 'active' && 'bg-v2-accent text-v2-on-accent',
      state === 'done' && 'bg-v2-success text-v2-surface',
      state === 'upcoming' &&
        'border-[1.5px] border-v2-line-strong bg-v2-surface text-v2-fg-4',
      className,
    )}
  >
    {state === 'done' ? (
      <svg
        key="check"
        width={14}
        height={14}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={clsx(celebrate && 'v2-pop-in')}
      >
        <path d="m3.5 8.5 3 3 6-7" />
      </svg>
    ) : (
      number
    )}
  </span>
);

/**
 * The journey in one line: small markers joined by a rail that fills up to
 * the open phase. For a summary card that points back to the full page.
 */
export const JourneyTrack: React.FC<{
  phases: ReadonlyArray<{ label: string; state: JourneyPhaseState }>;
  className?: string;
}> = ({ phases, className }) => {
  // More than one phase can be active (an earlier one only waiting). The
  // current step is the furthest of them, so the track never announces two.
  let current = -1;
  phases.forEach((phase, i) => {
    if (phase.state === 'active') current = i;
  });
  return (
    <ol
      aria-label="Steps"
      className={clsx('flex items-center gap-2', className)}
    >
      {phases.map((phase, i) => (
        <li
          key={phase.label}
          aria-current={i === current ? 'step' : undefined}
          className={clsx(
            'flex items-center gap-2',
            i < phases.length - 1 && 'flex-1',
          )}
        >
          <PhaseMarker number={i + 1} state={phase.state} size="sm" />
          <span
            className={clsx(
              'whitespace-nowrap text-v2-12 font-semibold',
              phase.state === 'upcoming' ? 'text-v2-fg-4' : 'text-v2-fg',
            )}
          >
            {phase.label}
            <span className="sr-only">
              {phase.state === 'done'
                ? ' (done)'
                : phase.state === 'active'
                  ? ' (in progress)'
                  : ' (not yet)'}
            </span>
          </span>
          {i < phases.length - 1 && (
            <span
              aria-hidden
              className={clsx(
                'mx-1 h-0.5 min-w-[16px] flex-1 rounded-full transition-colors duration-300',
                phase.state === 'done' ? 'bg-v2-success/60' : 'bg-v2-line',
              )}
            />
          )}
        </li>
      ))}
    </ol>
  );
};

/**
 * A block that opens and closes in place. Content stays mounted so its
 * state survives a fold; while closed it is hidden from assistive
 * technology and the tab order.
 */
export const Collapse: React.FC<{
  open: boolean;
  id?: string;
  className?: string;
  children: React.ReactNode;
}> = ({ open, id, className, children }) => (
  <div
    id={id}
    data-open={open}
    aria-hidden={!open || undefined}
    // `inert` isn't in React 18's typings yet; the attribute is what matters.
    {...(open ? {} : ({ inert: '' } as Record<string, string>))}
    className={clsx('v2-collapse', className)}
  >
    <div>{children}</div>
  </div>
);

export const JourneyPhase: React.FC<{
  number: number;
  label: string;
  state: JourneyPhaseState;
  /** Right of the label while open or done: "2 of 3 done", "All done". */
  meta?: React.ReactNode;
  /** Under the label while upcoming: why the phase is still closed. */
  unlockHint?: React.ReactNode;
  /** One quiet line while upcoming, instead of the steps: what the phase will ask. */
  preview?: React.ReactNode;
  /** Done phases fold by default; pass `true` to start them open. */
  defaultOpen?: boolean;
  /**
   * Keep the phase open while true (one of its steps is open). A phase
   * that finishes while held folds once it's let go, so it closes behind
   * the store as the flow moves on rather than under them.
   */
  holdOpen?: boolean;
  /** Place in the entrance sequence (0-based). */
  index?: number;
  /** The phase just finished: the marker settles in. */
  celebrate?: boolean;
  'aria-label'?: string;
  className?: string;
  children?: React.ReactNode;
}> = ({
  number,
  label,
  state,
  meta,
  unlockHint,
  preview,
  defaultOpen,
  holdOpen = false,
  index = 0,
  celebrate,
  'aria-label': ariaLabel,
  className,
  children,
}) => {
  const bodyId = useId();
  const [open, setOpen] = useState(
    defaultOpen ?? (state !== 'done' || holdOpen),
  );
  // A phase finishing during the visit folds; one reopening unfolds.
  const previous = useRef(state);
  const foldWhenLetGo = useRef(false);
  useEffect(() => {
    if (previous.current === state) return;
    previous.current = state;
    if (defaultOpen !== undefined) return;
    if (state === 'done' && holdOpen) {
      foldWhenLetGo.current = true;
      return;
    }
    setOpen(state !== 'done');
  }, [state, defaultOpen, holdOpen]);
  useEffect(() => {
    if (holdOpen) {
      setOpen(true);
      return;
    }
    if (!foldWhenLetGo.current) return;
    foldWhenLetGo.current = false;
    setOpen(false);
  }, [holdOpen]);

  const upcoming = state === 'upcoming';
  const showBody = state === 'active' || (state === 'done' && open);

  return (
    <Surface
      as="section"
      aria-label={ariaLabel ?? `${number} · ${label}`}
      data-state={state}
      style={{ '--v2-i': index } as React.CSSProperties}
      className={clsx(
        'v2-enter transition-shadow duration-200',
        state === 'active' && 'ring-1 ring-v2-accent/20',
        upcoming && 'bg-v2-sunken/50 shadow-none',
        className,
      )}
    >
      <div
        className={clsx(
          'flex items-center gap-3.5 px-5 py-4',
          showBody && 'border-b border-v2-line',
        )}
      >
        <PhaseMarker number={number} state={state} celebrate={celebrate} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <h2
              className={clsx(
                'text-v2-16 font-bold tracking-[-0.01em]',
                upcoming ? 'text-v2-fg-3' : 'text-v2-fg',
              )}
            >
              {label}
            </h2>
            {!upcoming && meta && (
              <span className="v2-num text-v2-13 text-v2-fg-3">{meta}</span>
            )}
          </div>
          {upcoming && unlockHint && (
            <p className="mt-0.5 flex items-center gap-1.5 text-v2-13 text-v2-fg-3">
              <IconLock size={13} className="shrink-0 text-v2-fg-4" />
              {unlockHint}
            </p>
          )}
          {upcoming && preview && (
            <p className="mt-0.5 truncate text-v2-13 text-v2-fg-4">{preview}</p>
          )}
        </div>
        {state === 'done' && (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-v2 px-2.5 text-v2-13 font-medium text-v2-fg-2 transition-colors hover:bg-v2-sunken hover:text-v2-fg"
          >
            {open ? 'Hide' : 'Show'}
            <IconChevronDown
              size={14}
              className={clsx(
                'transition-transform duration-200 motion-reduce:transition-none',
                open && 'rotate-180',
              )}
            />
          </button>
        )}
      </div>
      {!upcoming && (
        <Collapse open={showBody} id={bodyId}>
          {children}
        </Collapse>
      )}
    </Surface>
  );
};

/** The done glyph, 20px. `animate` draws it in once, for a step just finished. */
export const StepCheck: React.FC<{
  size?: number;
  animate?: boolean;
  className?: string;
}> = ({ size = 20, animate, className }) => (
  <svg
    aria-hidden
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={clsx(animate && 'v2-check-draw', className)}
  >
    <circle cx="10" cy="10" r="8.75" />
    <path d="m6.25 10.5 2.5 2.5 5-6" />
  </svg>
);

/** The not-yet glyph: an empty ring the check will fill. */
export const StepRing: React.FC<{
  size?: number;
  /**
   * `accent` and `danger` are open-step rings in a grid that colours its
   * glyphs: accent for an ordinary open step, danger for one that asks to
   * be acted on.
   */
  tone?: 'default' | 'muted' | 'accent' | 'danger';
  className?: string;
}> = ({ size = 20, tone = 'default', className }) => (
  <span
    aria-hidden
    style={{ width: size, height: size }}
    className={clsx(
      'block shrink-0 rounded-full border-[1.75px]',
      tone === 'muted'
        ? 'border-v2-line-strong'
        : tone === 'accent'
          ? 'border-v2-accent'
          : tone === 'danger'
            ? 'border-v2-danger'
            : 'border-v2-fg-4',
      className,
    )}
  />
);

/** The waiting glyph: a clock in the ring — done here, with Ask Arthur now. */
export const StepWait: React.FC<{ size?: number; className?: string }> = ({
  size = 20,
  className,
}) => (
  <svg
    aria-hidden
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="10" cy="10" r="8.75" />
    <path d="M10 5.75V10l2.75 1.75" />
  </svg>
);

/** The not-needed glyph: a short dash — the step doesn't apply here. */
export const StepNone: React.FC<{ size?: number; className?: string }> = ({
  size = 20,
  className,
}) => (
  <span
    aria-hidden
    style={{ width: size, height: size }}
    className={clsx('flex shrink-0 items-center justify-center', className)}
  >
    <span className="h-[2px] w-[60%] rounded-full bg-current" />
  </span>
);

/**
 * GlyphLegend: what the glyphs in a dense grid mean, under the grid. The
 * glyphs are decorative here and in the grid — give each cell its own
 * accessible name (`aria-label` on the cell's glyph wrapper).
 */
export const GlyphLegend: React.FC<{
  items: { glyph: React.ReactNode; label: string }[];
  className?: string;
}> = ({ items, className }) => (
  <ul
    className={clsx(
      'flex flex-wrap items-center gap-x-5 gap-y-2 text-v2-12 text-v2-fg-3',
      className,
    )}
  >
    {items.map((item) => (
      <li key={item.label} className="flex items-center gap-1.5">
        {item.glyph}
        {item.label}
      </li>
    ))}
  </ul>
);

export const JourneyRow: React.FC<{
  glyph: React.ReactNode;
  title: React.ReactNode;
  detail?: React.ReactNode;
  /** A line from the team, under the detail. */
  note?: React.ReactNode;
  /** The step's status, as a `StatusPill`. */
  status?: React.ReactNode;
  action?: React.ReactNode;
  /** Done and waiting rows read quieter. */
  muted?: boolean;
  /**
   * Where the step is done, opened in place under the row. With a panel the
   * title becomes the disclosure button (the WAI-ARIA accordion pattern);
   * the panel mounts the first time it opens and stays mounted after, so a
   * half-filled form survives closing it.
   */
  panel?: React.ReactNode;
  open?: boolean;
  onToggle?: () => void;
  /** The step just finished: the row washes green once. */
  justDone?: boolean;
  rowRef?: React.Ref<HTMLLIElement>;
  className?: string;
}> = ({
  glyph,
  title,
  detail,
  note,
  status,
  action,
  muted,
  panel,
  open = false,
  onToggle,
  justDone,
  rowRef,
  className,
}) => {
  const panelId = useId();
  const expandable = panel !== undefined && Boolean(onToggle);
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  return (
    <li
      ref={rowRef}
      data-open={expandable ? open : undefined}
      className={clsx(
        'v2-step relative scroll-mt-4 border-t border-v2-line/80 first:border-t-0',
        justDone && 'v2-step-done',
        className,
      )}
    >
      {expandable && (
        <span
          aria-hidden
          className="v2-step-rail absolute inset-y-0 left-0 w-[3px] rounded-r-full bg-v2-accent"
        />
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5 px-5 py-4">
        <div
          className={clsx(
            'flex min-w-0 flex-[1_1_300px] items-start gap-3.5',
            expandable && 'cursor-pointer',
          )}
          // The heading's button is the control; the rest of the block is
          // a larger mouse target for it.
          onClick={expandable ? onToggle : undefined}
        >
          <span className="mt-px shrink-0">{glyph}</span>
          <div className="min-w-0">
            <h3
              className={clsx(
                'text-v2-14 font-semibold',
                muted && !open ? 'text-v2-fg-2' : 'text-v2-fg',
              )}
            >
              {expandable ? (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggle?.();
                  }}
                  className="rounded-[4px] text-left outline-none focus-visible:ring-2 focus-visible:ring-v2-accent/40 focus-visible:ring-offset-2"
                >
                  {title}
                </button>
              ) : (
                title
              )}
            </h3>
            {detail && (
              <p className="mt-0.5 text-v2-13 text-v2-fg-3">{detail}</p>
            )}
            {note && <p className="mt-1 text-v2-13 text-v2-fg-2">{note}</p>}
          </div>
        </div>
        {(status || action) && (
          <div className="ml-auto flex shrink-0 items-center gap-3 pl-[34px] sm:pl-0">
            {status}
            {action}
          </div>
        )}
      </div>
      {expandable && (
        <Collapse open={open} id={panelId}>
          <div className="v2-step-panel px-5 pb-6 sm:pl-[54px]">
            {mounted && panel}
          </div>
        </Collapse>
      )}
    </li>
  );
};

/**
 * Which of `completed` were not completed when the component first
 * rendered: the steps finished during this visit, whose checks should be
 * drawn in rather than already there.
 */
export function useJustCompleted(
  completed: readonly string[],
): ReadonlySet<string> {
  const seen = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set());
  const signature = completed.join('\u0000');
  useEffect(() => {
    const keys = signature ? signature.split('\u0000') : [];
    if (seen.current === null) {
      seen.current = new Set(keys);
      return;
    }
    const known = seen.current;
    const added = keys.filter((key) => !known.has(key));
    keys.forEach((key) => known.add(key));
    if (added.length) {
      setFresh((previous) => new Set(Array.from(previous).concat(added)));
    }
  }, [signature]);
  return fresh;
}
